import { __rest } from "tslib";
import { useField } from 'formik';
import * as React from 'react';
import { IconButton } from '../../icon-button';
import { FieldErrorMessageWrapper, InputStepperWrapper, LabelSupportingTextWrapper, StyledInputStepper, StyledInternalTextInput, StyledLabel, StyledMinusButton, StyledPlusButton, StyledSupportingLabel, } from './InputStepper.styled';
const MINUTES_IN_HOUR = 60;
const MINUTES_IN_DAY = 1440;
const MIDNIGHT = '00:00';
export const TimeInputStepper = (_a) => {
    var { incrementAmountMinutes = 15, minTimeMinutes = 0, maxTimeMinutes = MINUTES_IN_DAY, layout = 'vertical', supportingText, inputWidth = '104px' } = _a, props = __rest(_a, ["incrementAmountMinutes", "minTimeMinutes", "maxTimeMinutes", "layout", "supportingText", "inputWidth"]);
    // Formik connector
    // https://formik.org/docs/api/useField
    const [field, meta, helpers] = useField(props);
    const { value } = field;
    const { setValue, setTouched } = helpers;
    // times can be stored with the hours as single digit in the backend (0:00 rather than 00:00), the time input requires HH:MM format
    const normaliseTime = (time) => {
        const singleDigitHoursRegex = /^\d:\d{2}$/;
        return singleDigitHoursRegex.test(time) ? `0${time}` : time;
    };
    /* the stepperDirection tracks whether the stepper was most recently
    incremented, decremented or if it hasn't been used yet. This is done to
    handle the time case of 00:00. If a user is incrementing, we want 00:00 to
    be the maxiumum time, and if they are decrementing, we want 00:00 to be the minimum time. */
    const [stepperDirection, setStepperDirection] = React.useState(null);
    const timeToMinutes = (timeString) => {
        const [hours, minutes] = timeString.split(':').map(Number);
        if (hours === undefined ||
            !isFinite(hours) ||
            minutes === undefined ||
            !isFinite(minutes))
            return 0;
        return hours * MINUTES_IN_HOUR + minutes;
    };
    const minutesToTime = (minutes) => {
        // 24 hours and 0 hours are the same time and should be displayed as 00:00
        if (minutes === MINUTES_IN_DAY || 0)
            return MIDNIGHT;
        const hours = Math.floor(minutes / MINUTES_IN_HOUR);
        const remainingMinutes = minutes % MINUTES_IN_HOUR;
        const hoursString = hours < 10 ? `0${hours}` : `${hours}`;
        const remainingMinutesString = remainingMinutes < 10
            ? `0${remainingMinutes}`
            : `${remainingMinutes}`;
        return `${hoursString}:${remainingMinutesString}`;
    };
    const decrementTimeValue = (newTime) => {
        // if the value has been incremented to midnight, we want to decrement from the max time, which is  24 hours (1440 minutes)
        const minutes = value === MIDNIGHT && stepperDirection === 'increment'
            ? MINUTES_IN_DAY
            : timeToMinutes(newTime);
        const calculatedMinValue = minTimeMinutes < 0 || minTimeMinutes > MINUTES_IN_DAY
            ? 0
            : minTimeMinutes;
        if (minutes <= calculatedMinValue)
            return;
        const calculatedNewValue = Math.max(minutes - incrementAmountMinutes, calculatedMinValue);
        void setValue(minutesToTime(calculatedNewValue));
    };
    const onDecrementHandler = () => {
        void setTouched(true);
        setStepperDirection('decrement');
        decrementTimeValue(String(value));
    };
    const isDecrementButtonDisabled = () => {
        // if the value is midnight, we disable the decrement button unless the value has incremented to midnight.
        if (String(value) === MIDNIGHT)
            return stepperDirection !== 'increment';
        const minutes = timeToMinutes(String(value));
        const calculatedMinValue = minTimeMinutes < 0 || minTimeMinutes > MINUTES_IN_DAY
            ? 0
            : minTimeMinutes;
        return minutes <= calculatedMinValue;
    };
    const incrementTimeValue = (newTime) => {
        const minutes = timeToMinutes(newTime);
        if (minutes >=
            Math.min(!maxTimeMinutes ? Infinity : maxTimeMinutes, MINUTES_IN_DAY))
            return;
        const calculatedNewValue = Math.min(minutes + incrementAmountMinutes, MINUTES_IN_DAY);
        void setValue(minutesToTime(calculatedNewValue));
    };
    const onIncrementHandler = () => {
        void setTouched(true);
        setStepperDirection('increment');
        incrementTimeValue(String(value));
    };
    const isIncrementerButtonDisabled = () => {
        if (String(value) === MIDNIGHT && stepperDirection === 'increment')
            return true;
        const minutes = timeToMinutes(String(value));
        return (minutes >=
            Math.min(!maxTimeMinutes ? Infinity : maxTimeMinutes, MINUTES_IN_DAY));
    };
    const preventInvalidInput = (e) => {
        void setTouched(true);
        const allowedNonNumericKeys = [
            'Backspace',
            'ArrowLeft',
            'ArrowRight',
            ' ', // space brings up the time picker
        ];
        if (allowedNonNumericKeys.includes(e.key))
            return;
        else if (!isFinite(e.key)) {
            e.preventDefault();
        }
        return;
    };
    return (React.createElement(StyledInputStepper, { layout: layout, className: props.className },
        React.createElement(LabelSupportingTextWrapper, { supportingText: supportingText, layout: layout },
            React.createElement(StyledLabel, null, props.label),
            supportingText && (React.createElement(StyledSupportingLabel, null, supportingText))),
        React.createElement(InputStepperWrapper, null,
            React.createElement(StyledMinusButton, null,
                React.createElement(IconButton, { "aria-label": "decrement-button", variant: "primary", fill: "light", size: "regular", icon: "Minus", onClick: onDecrementHandler, disabled: isDecrementButtonDisabled() })),
            React.createElement(FieldErrorMessageWrapper, { inputWidth: inputWidth },
                React.createElement(StyledInternalTextInput, Object.assign({}, field, meta, { value: normaliseTime(String(value)), type: "time", onKeyDown: preventInvalidInput }, props))),
            React.createElement(StyledPlusButton, null,
                React.createElement(IconButton, { "aria-label": "increment-button", variant: "primary", fill: "light", size: "regular", icon: "Plus", onClick: onIncrementHandler, disabled: isIncrementerButtonDisabled() })))));
};
//# sourceMappingURL=TimeInputStepper.js.map